import React from 'react'
import PropTypes from 'prop-types'
import { Controller } from 'react-hook-form'
import Stack from 'components/mui/Stack'
import AppTextFieldComponent from 'components/mui/AppTextFieldComponent'
import AppSelectComponent from 'components/mui/AppSelectComponent'
import {
  OCCUPATION_OPTIONS,
  INCOME_SLAB_OPTIONS
} from 'src/constants/kyc-profile.constants'
import {
  KYC_FATCA_INPUT_STYLES,
  KYC_FATCA_RULES,
  withCurrentSelectOption,
  sanitizeIncomeSlabValue
} from 'utils/kyc-fatca.utils'

/**
 * Shared editable FATCA fields used by post-confirm and Edit KYC modals.
 */
const KycFatcaFieldsForm = ({
  control,
  errors = {},
  disabled = false,
  inputSx = KYC_FATCA_INPUT_STYLES
}) => {
  if (!control) return null

  return (
    <Stack spacing={2.5}>
      <Controller
        name="birthPlace"
        control={control}
        rules={KYC_FATCA_RULES?.birthPlace ?? {}}
        render={({ field }) => (
          <AppTextFieldComponent
            name={field?.name}
            value={field?.value ?? ''}
            onChange={field?.onChange}
            onBlur={field?.onBlur}
            inputRef={field?.ref}
            label="Place of Birth"
            placeholder="Enter place of birth"
            error={!!errors?.birthPlace}
            helperText={errors?.birthPlace?.message}
            required
            fullWidth
            disabled={!!disabled}
            InputLabelProps={{ shrink: true }}
            sx={inputSx}
          />
        )}
      />

      <Controller
        name="occupation"
        control={control}
        rules={KYC_FATCA_RULES?.occupation ?? {}}
        render={({ field }) => (
          <AppSelectComponent
            name={field?.name}
            value={field?.value ?? ''}
            onChange={(event) => {
              const nextValue = event?.target?.value ?? ''
              field?.onChange?.(nextValue)
            }}
            onBlur={field?.onBlur}
            options={withCurrentSelectOption?.(OCCUPATION_OPTIONS ?? [], field?.value) ?? []}
            label="Occupation"
            placeholder="Select occupation"
            error={!!errors?.occupation}
            helperText={errors?.occupation?.message}
            required
            fullWidth
            disabled={!!disabled}
            InputLabelProps={{ shrink: true }}
            sx={inputSx}
          />
        )}
      />

      <Controller
        name="incomeSlab"
        control={control}
        rules={KYC_FATCA_RULES?.incomeSlab ?? {}}
        render={({ field }) => {
          // "00" from BE means no slab on record - keep the select empty so the user picks one
          const incomeValue = sanitizeIncomeSlabValue?.(field?.value) ?? ''
          return (
            <AppSelectComponent
              name={field?.name}
              value={incomeValue}
              onChange={(event) => {
                const nextValue = event?.target?.value ?? ''
                field?.onChange?.(nextValue)
              }}
              onBlur={field?.onBlur}
              options={withCurrentSelectOption?.(INCOME_SLAB_OPTIONS ?? [], incomeValue) ?? []}
              label="Annual Income"
              placeholder="Select annual income"
              error={!!errors?.incomeSlab}
              helperText={errors?.incomeSlab?.message}
              required
              fullWidth
              disabled={!!disabled}
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
          )
        }}
      />
    </Stack>
  )
}

KycFatcaFieldsForm.propTypes = {
  control: PropTypes.object.isRequired,
  errors: PropTypes.object,
  disabled: PropTypes.bool,
  inputSx: PropTypes.object
}

KycFatcaFieldsForm.defaultProps = {
  errors: {},
  disabled: false,
  inputSx: KYC_FATCA_INPUT_STYLES
}

export default KycFatcaFieldsForm
